import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';

const freehandProps = z.object({
  // Prefer top-level points but also allow nested under attrs for legacy writes
  points: z.array(z.number()).default([]),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  opacity: z.number().optional(),
  lineCap: z.enum(['butt', 'round', 'square']).optional(),
  lineJoin: z.enum(['miter', 'round', 'bevel']).optional(),
  tension: z.number().optional(),
  // Some tools write into attrs; keep it flexible for export
  attrs: z.record(z.string(), z.any()).optional(),
});

export type FreehandProps = z.infer<typeof freehandProps>;

function coalesce<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const v of values) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function asNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

function toPointsArray(value: unknown): number[] {
  if (Array.isArray(value)) {
    const arr = value
      .map((v) => (typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN))
      .filter((n) => Number.isFinite(n)) as number[];
    return arr.length >= 2 ? arr : [];
  }
  return [];
}

export const CommonFreehandManifest: ShapeManifest<FreehandProps> = {
  id: 'freehand',
  version: '1.0.0',
  category: 'annotation',
  propsSchema: freehandProps,
  defaultProps: () => ({ points: [] }),
  // Freehand strokes are drawn in absolute coordinates; treat as non-transformable widget
  capabilities: { selectable: true, transformable: false },
  renderers: {
    svg: ({ viewModel, props }) => {
      const style = (viewModel?.style ?? {}) as Record<string, unknown>;
      const attrs = (props.attrs ?? {}) as Record<string, unknown>;

      // Accept points from props.points or attrs.points (how tools write today)
      const rawPoints = toPointsArray(props.points) || toPointsArray(attrs.points);
      if (rawPoints.length < 4) return '';

      // Normalize to local coordinates so outer group transforms don't displace the stroke
      let minX = Infinity;
      let minY = Infinity;
      for (let i = 0; i < rawPoints.length; i += 2) {
        const x = rawPoints[i] as number;
        const y = rawPoints[i + 1] as number;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
        }
      }
      if (!Number.isFinite(minX) || !Number.isFinite(minY)) return '';

      const localPoints: number[] = [];
      for (let i = 0; i < rawPoints.length; i += 2) {
        localPoints.push((rawPoints[i] as number) - minX, (rawPoints[i + 1] as number) - minY);
      }

      const stroke =
        (coalesce(props.strokeColor, attrs.strokeColor, style.strokeColor, style.stroke) as string | undefined) ||
        '#000000';
      const strokeWidth =
        asNumber(coalesce(props.strokeWidth, attrs.strokeWidth, style.strokeWidth)) ?? 2;
      const opacity = asNumber(coalesce(props.opacity, attrs.opacity, style.opacity)) ?? 1;
      const lineCap = (coalesce(props.lineCap, attrs.lineCap, style.lineCap) as string | undefined) || 'round';
      const lineJoin = (coalesce(props.lineJoin, attrs.lineJoin, style.lineJoin) as string | undefined) || 'round';

      // Build polyline string
      const pointsStr: string = localPoints
        .map((v, idx) => (idx % 2 === 0 ? `${v}` : `,${v}`))
        .join(' ');

      // Translate to the absolute origin of the stroke
      return (
        `<g transform="translate(${minX}, ${minY})">` +
        `<polyline points="${pointsStr}" fill="none" stroke="${escapeAttr(stroke)}" stroke-width="${strokeWidth}" stroke-linecap="${escapeAttr(lineCap)}" stroke-linejoin="${escapeAttr(lineJoin)}" opacity="${opacity}" />` +
        `</g>`
      );
    },
  },
};

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&apos;');
}
